import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Zap, MessageSquare, DollarSign, Trophy } from "lucide-react";

const PLACEHOLDERS = [
  { key: "first_name", label: "First name" },
  { key: "full_name", label: "Full name" },
  { key: "discount", label: "Discount %" },
  { key: "coupon_code", label: "Coupon code" },
  { key: "services", label: "Declined services" },
  { key: "vehicle", label: "Year make model" },
  { key: "dealership", label: "Dealership name" },
  { key: "phone", label: "Live person phone" },
  { key: "advisor_name", label: "Advisor name" },
  { key: "csi_manager_name", label: "CSI manager name" },
  { key: "csi_manager_phone", label: "CSI manager phone" },
  { key: "schedule_url", label: "Schedule link" },
  { key: "call_url", label: "Call link" },
];

const PREVIEW_CTX = {
  first_name: "Alicia",
  full_name: "Alicia Bennett",
  services: "Brake Pads Replacement, Rotor Resurface",
  vehicle: "2021 Toyota RAV4",
  coupon_code: "DD-30-A7B2K9",
  advisor_name: "Jayne",
  schedule_url: "https://…/schedule/abc123",
  call_url: "https://…/call/abc123",
};

function renderPreview(tpl, extra) {
  let out = tpl || "";
  const ctx = { ...PREVIEW_CTX, ...extra };
  Object.entries(ctx).forEach(([k, v]) => {
    out = out.split("{" + k + "}").join(String(v));
  });
  // CTIA STOP disclaimer on its own line — never trailing a shortlink.
  const stop = "Reply STOP to opt out.";
  const trimmed = out.replace(/\s+Reply STOP to opt out\.\s*$/i, "").trimEnd();
  return `${trimmed}\n\n${stop}`;
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null); // for dirty-check
  const [saving, setSaving] = useState(false);
  const [consentFile, setConsentFile] = useState(null);
  const [consentUploading, setConsentUploading] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setS(r.data);
        setSavedSnapshot(JSON.stringify(r.data));
      })
      .catch((e) => toast.error(formatError(e.response?.data?.detail)));
  }, []);

  const dirty = useMemo(
    () => !!s && savedSnapshot && JSON.stringify(s) !== savedSnapshot,
    [s, savedSnapshot]
  );

  const preview30 = useMemo(
    () =>
      renderPreview(s?.sms_template_stage_1, {
        discount: s?.stage_1_discount ?? 30,
        dealership: s?.dealership_name || "Your Dealership",
        phone: s?.dealership_phone || "",
      }),
    [s]
  );
  const preview40 = useMemo(
    () =>
      renderPreview(s?.sms_template_stage_2, {
        discount: s?.stage_2_discount ?? 40,
        dealership: s?.dealership_name || "Your Dealership",
        phone: s?.dealership_phone || "",
      }),
    [s]
  );

  const previewOil = useMemo(
    () =>
      renderPreview(s?.sms_template_oil_change, {
        dealership: s?.dealership_name || "Your Dealership",
        coupon_code: `${(s?.coupon_prefix || "DD").toUpperCase()}-OIL-A7B2K9`,
        advisor_name: s?.default_parts_advisor || "Jayne",
      }),
    [s]
  );
  const previewParts = useMemo(
    () =>
      renderPreview(s?.sms_template_parts_arrived, {
        dealership: s?.dealership_name || "Your Dealership",
        advisor_name: s?.default_parts_advisor || "Jayne",
      }),
    [s]
  );
  const previewCsi = useMemo(
    () =>
      renderPreview(s?.sms_template_csi, {
        dealership: s?.dealership_name || "Your Dealership",
        csi_manager_name: s?.csi_manager_name || "Tom Tillman",
        csi_manager_phone: s?.csi_manager_phone || "987-4378-8978",
      }),
    [s]
  );
  const previewBody = useMemo(
    () =>
      renderPreview(s?.sms_template_body_shop, {
        dealership: s?.dealership_name || "Your Dealership",
        discount: s?.body_shop_discount ?? 10,
        coupon_code: `${(s?.coupon_prefix || "DD").toUpperCase()}-BODY-A7B2K9`,
      }),
    [s]
  );

  if (!s)
    return (
      <DashboardShell>
        <div className="text-sm text-muted-foreground">Loading…</div>
      </DashboardShell>
    );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        dealership_name: s.dealership_name,
        dealership_phone: s.dealership_phone,
        dealership_logo_url: s.dealership_logo_url || "",
        stage_1_days: Number(s.stage_1_days),
        stage_1_discount: Number(s.stage_1_discount),
        stage_2_days: Number(s.stage_2_days),
        stage_2_discount: Number(s.stage_2_discount),
        comeback_day_30_days: Number(s.comeback_day_30_days ?? 30),
        comeback_day_60_days: Number(s.comeback_day_60_days ?? 60),
        comeback_day_90_days: Number(s.comeback_day_90_days ?? 90),
        comeback_discount_final: Number(s.comeback_discount_final ?? 50),
        sms_template_stage_1: s.sms_template_stage_1,
        sms_template_stage_2: s.sms_template_stage_2,
        fee_percent: Number(s.fee_percent ?? 0),
        monthly_fee: Number(s.monthly_fee ?? 1599),
        contract_months: Number(s.contract_months ?? 12),
        onboarding_fee: Number(s.onboarding_fee ?? 895),
        onboarding_billed: Boolean(s.onboarding_billed),
        remit_to_name: s.remit_to_name || "Ayers Enterprises",
        remit_to_address: s.remit_to_address || "",
        remit_ach_bank: s.remit_ach_bank || "",
        remit_ach_routing: s.remit_ach_routing || "",
        remit_ach_account: s.remit_ach_account || "",
        owner_name: s.owner_name || "",
        owner_phone: s.owner_phone || "",
        owner_email: s.owner_email || "",
        demo_mode: s.demo_mode !== false,
        coupon_prefix: (s.coupon_prefix || "DD").toUpperCase().slice(0, 8),
        coupon_include_name: Boolean(s.coupon_include_name),
        sms_template_oil_change: s.sms_template_oil_change || "",
        sms_template_parts_arrived: s.sms_template_parts_arrived || "",
        sms_template_csi: s.sms_template_csi || "",
        sms_template_body_shop: s.sms_template_body_shop || "",
        body_shop_discount: Number(s.body_shop_discount ?? 10),
        body_shop_enabled: Boolean(s.body_shop_enabled),
        csi_manager_name: s.csi_manager_name || "",
        csi_manager_phone: s.csi_manager_phone || "",
        csi_auto_send_hours: Number(s.csi_auto_send_hours ?? 24),
        csi_auto_enabled: s.csi_auto_enabled !== false,
        default_parts_advisor: s.default_parts_advisor || "Jayne",
        dealer_legal_name: s.dealer_legal_name || "",
        dealer_address: s.dealer_address || "",
        service_director_name: s.service_director_name || "",
        service_director_email: s.service_director_email || "",
        service_director_phone: s.service_director_phone || "",
        service_manager_name: s.service_manager_name || "",
        service_manager_phone: s.service_manager_phone || "",
        dms_platform: s.dms_platform || "",
        dms_method: s.dms_method || "",
      });
      // Refresh state from server so any coercion (int/float) is reflected
      // and the dirty check resets to clean.
      setS(data);
      setSavedSnapshot(JSON.stringify(data));
      toast.success(
        `Settings saved · Stage 1 = ${data.stage_1_discount}% · Stage 2 = ${data.stage_2_discount}%`
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const quickSaveDiscounts = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        stage_1_days: Number(s.stage_1_days),
        stage_1_discount: Number(s.stage_1_discount),
        stage_2_days: Number(s.stage_2_days),
        stage_2_discount: Number(s.stage_2_discount),
        comeback_day_30_days: Number(s.comeback_day_30_days ?? 30),
        comeback_day_60_days: Number(s.comeback_day_60_days ?? 60),
        comeback_day_90_days: Number(s.comeback_day_90_days ?? 90),
        comeback_discount_final: Number(s.comeback_discount_final ?? 50),
      });
      setS(data);
      setSavedSnapshot(JSON.stringify(data));
      toast.success(
        `Discounts saved · Day ${data.stage_1_days} = ${data.stage_1_discount}% · Day ${data.stage_2_days} = ${data.stage_2_discount}%`
      );
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardShell>
      {/* STICKY DIRTY-STATE SAVE BAR — appears the moment anything changes so
          James can never blow another deal because the Save button was buried
          1000 lines deep in the page. */}
      {dirty && (
        <div
          className="sticky top-0 z-40 -mx-4 sm:-mx-6 md:-mx-8 mb-4 px-4 sm:px-6 md:px-8 py-3 bg-[hsl(var(--gold))] border-b-2 border-[hsl(var(--gold-dark,var(--gold)))] shadow-lg flex items-center justify-between gap-4 no-print"
          data-testid="settings-dirty-bar"
        >
          <div className="text-neutral-900">
            <div className="font-mono-label text-[0.6rem] tracking-widest">Unsaved changes</div>
            <div className="font-display font-bold text-sm">
              You have unsaved edits — hit Save so the demo shows the right numbers.
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => {
                setS(JSON.parse(savedSnapshot));
                toast.info("Changes discarded");
              }}
              className="bg-white text-neutral-900 border-neutral-900 hover:bg-neutral-100"
              data-testid="settings-discard-btn"
              disabled={saving}
            >
              Discard
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-navy text-white hover:bg-navy/90"
              data-testid="settings-sticky-save-btn"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      )}

      {/* Best-converting templates — pulled out of the buried list below so
          James can tune the two money-makers first (Stage 1 · Day {s.stage_1_days} · {s.stage_1_discount}% and
          Stage 2 · Day {s.stage_2_days} · {s.stage_2_discount}%). Both textareas are bound to the same state
          as the deeper editors — no drift, no duplication. */}
      <Card className="p-5 mb-6 border-2 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/5 max-w-4xl" data-testid="best-templates-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-md bg-[hsl(var(--gold))] grid place-items-center shrink-0">
            <Trophy className="w-4 h-4 text-neutral-900" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-mono-label text-[hsl(var(--gold))] text-[0.6rem] tracking-widest">
              Your offer texts
            </div>
            <div className="font-display font-bold text-base tracking-tight">
              Your offer texts — tune the wording here
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="best-t1" className="font-mono-label">Offer 1 · Day {s.stage_1_days} · {s.stage_1_discount ?? 30}% OFF</Label>
              <Badge className="bg-blue-600 text-white text-[0.55rem]">OFFER 1</Badge>
            </div>
            <Textarea
              id="best-t1"
              rows={4}
              value={s.sms_template_stage_1 || ""}
              onChange={(e) => setS({ ...s, sms_template_stage_1: e.target.value })}
              className="font-mono text-xs"
              data-testid="best-template-1"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="best-t2" className="font-mono-label">Offer 2 · Day {s.stage_2_days} · {s.stage_2_discount ?? 40}% OFF</Label>
              <Badge className="bg-orange-500 text-white text-[0.55rem]">OFFER 2</Badge>
            </div>
            <Textarea
              id="best-t2"
              rows={4}
              value={s.sms_template_stage_2 || ""}
              onChange={(e) => setS({ ...s, sms_template_stage_2: e.target.value })}
              className="font-mono text-xs"
              data-testid="best-template-2"
            />
          </div>
        </div>
        <div className="text-[0.65rem] text-muted-foreground mt-3">
          Changes here sync with the full template list below. Save at the bottom of the page.
        </div>
      </Card>

      <div className="mb-8">
        <div className="font-mono-label text-muted-foreground">Configuration</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Campaign Settings</h1>
      </div>

      <Card
        className="p-5 border-border max-w-4xl mb-6 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900"
        data-testid="cron-status-card"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-emerald-600 grid place-items-center shrink-0">
            <Zap className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="font-display font-bold">Nightly Auto-Send is Active</div>
              <Badge className="bg-emerald-600 text-white">ON</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Every night Decline Drive prepares the day&apos;s follow-ups — Day{" "}
              {s.stage_1_days} ({s.stage_1_discount}%) and Day {s.stage_2_days} ({s.stage_2_discount}%) offers.
              Texts go out during your quiet-hours window
              {s.quiet_hours_timezone ? ` (${s.quiet_hours_timezone.replace("_", " ")})` : ""}.
              The &quot;Run Campaign Cycle&quot; button on the dashboard is optional and only for
              catching up mid-day.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border max-w-4xl mb-6 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900" data-testid="reset-demo-card" style={{ display: (user?.role === "platform_admin" || s?.demo_mode !== false) ? undefined : "none" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-bold text-red-800 dark:text-red-300">Reset Demo Data</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Wipes all declines, appointments, and SMS messages. Keeps your settings, advisors, and login.
              Use this to give a fresh demo to your next dealer.
              {" "}Live dealer accounts never see this card.
            </p>
            {resetOpen && (
              <div className="mt-3 flex items-center gap-2" data-testid="reset-demo-confirm-row">
                <Input
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  placeholder="Type RESET to confirm"
                  className="w-52 font-mono"
                  data-testid="reset-demo-confirm-input"
                />
                <Button
                  variant="outline"
                  onClick={() => { setResetOpen(false); setResetConfirm(""); }}
                  data-testid="reset-demo-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  disabled={resetConfirm !== "RESET"}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
                  data-testid="reset-demo-confirm-btn"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/demo/reset");
                      toast.success(`Reset — wiped ${data.declines_wiped} declines, ${data.sms_wiped} messages`);
                      setResetOpen(false);
                      setResetConfirm("");
                    } catch (e) {
                      toast.error(formatError(e.response?.data?.detail));
                    }
                  }}
                >
                  Wipe everything
                </Button>
              </div>
            )}
          </div>
          {!resetOpen && (
            <Button
              variant="outline"
              className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white shrink-0"
              data-testid="reset-demo-btn"
              onClick={() => setResetOpen(true)}
            >
              Reset all demo data
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-5 border-border max-w-4xl mb-6" data-testid="demo-mode-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-bold">Demo Mode</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              When ON, the dashboard shows &quot;Boost Demo&quot; and &quot;Nightly Import&quot; buttons for
              sales demos. Turn OFF once a real dealer is using this rooftop so those buttons disappear.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={s.demo_mode !== false}
              onChange={(e) => setS({ ...s, demo_mode: e.target.checked })}
              className="w-5 h-5"
              data-testid="setting-demo-mode"
            />
            <span className="font-mono-label">{s.demo_mode !== false ? "ON" : "OFF"}</span>
          </label>
        </div>
      </Card>

      {/* Dealer Info — shows in the left sidebar */}
      <Card className="p-6 border-border max-w-4xl mb-6" data-testid="provider-info-card">
        <div className="font-mono-label text-muted-foreground mb-1">Your Info</div>
        <div className="font-display text-lg font-bold tracking-tight mb-1">Provider Contact (You)</div>
        <div className="text-xs text-muted-foreground mb-4">
          These name/email/phone values appear on the printable Contract, API Letter, Flyer, and
          Example Sheet. Edit here anytime.
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <div>
            <Label htmlFor="on" className="font-mono-label">Your Name</Label>
            <Input
              id="on"
              value={s.owner_name || ""}
              onChange={(e) => setS({ ...s, owner_name: e.target.value })}
              placeholder="James Ayers"
              className="mt-1.5"
              data-testid="setting-owner-name"
            />
          </div>
          <div>
            <Label htmlFor="op" className="font-mono-label">Your Phone</Label>
            <Input
              id="op"
              value={s.owner_phone || ""}
              onChange={(e) => setS({ ...s, owner_phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="mt-1.5"
              data-testid="setting-owner-phone"
            />
          </div>
          <div>
            <Label htmlFor="oe" className="font-mono-label">Your Email</Label>
            <Input
              id="oe"
              value={s.owner_email || ""}
              onChange={(e) => setS({ ...s, owner_email: e.target.value })}
              placeholder="you@example.com"
              className="mt-1.5"
              data-testid="setting-owner-email"
            />
          </div>
        </div>
      </Card>

      {/* Dealer Info — shows in the left sidebar */}
      <Card className="p-6 border-border max-w-4xl mb-6" data-testid="dealer-info-card">
        <div className="font-mono-label text-muted-foreground mb-1">Onboarding Info</div>
        <div className="font-display text-lg font-bold tracking-tight mb-1">Dealer Information</div>
        <div className="text-xs text-muted-foreground mb-4">
          Shows in the sidebar and pre-fills the printable Contract, API Letter, and Statement.
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label htmlFor="dln" className="font-mono-label">Legal Name (for contracts)</Label>
            <Input
              id="dln"
              value={s.dealer_legal_name || ""}
              onChange={(e) => setS({ ...s, dealer_legal_name: e.target.value })}
              placeholder="Precision Auto Group, LLC"
              className="mt-1.5"
              data-testid="setting-dealer-legal"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="daddr" className="font-mono-label">Physical Address</Label>
            <textarea
              id="daddr"
              rows={3}
              value={s.dealer_address || ""}
              onChange={(e) => setS({ ...s, dealer_address: e.target.value })}
              placeholder={"123 Auto Row\nDallas, TX 75201"}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
              data-testid="setting-dealer-address"
            />
          </div>
          <div>
            <Label htmlFor="sdn" className="font-mono-label">Service Director Name</Label>
            <Input
              id="sdn"
              value={s.service_director_name || ""}
              onChange={(e) => setS({ ...s, service_director_name: e.target.value })}
              className="mt-1.5"
              data-testid="setting-sd-name"
            />
          </div>
          <div>
            <Label htmlFor="sdp" className="font-mono-label">Service Director Phone</Label>
            <Input
              id="sdp"
              value={s.service_director_phone || ""}
              onChange={(e) => setS({ ...s, service_director_phone: e.target.value })}
              className="mt-1.5"
              data-testid="setting-sd-phone"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="sde" className="font-mono-label">Service Director Email</Label>
            <Input
              id="sde"
              type="email"
              value={s.service_director_email || ""}
              onChange={(e) => setS({ ...s, service_director_email: e.target.value })}
              className="mt-1.5"
              data-testid="setting-sd-email"
            />
          </div>
          <div>
            <Label htmlFor="smn" className="font-mono-label">Service Manager Name</Label>
            <Input
              id="smn"
              value={s.service_manager_name || ""}
              onChange={(e) => setS({ ...s, service_manager_name: e.target.value })}
              className="mt-1.5"
              data-testid="setting-sm-name"
            />
          </div>
          <div>
            <Label htmlFor="smp" className="font-mono-label">Service Manager Phone</Label>
            <Input
              id="smp"
              value={s.service_manager_phone || ""}
              onChange={(e) => setS({ ...s, service_manager_phone: e.target.value })}
              className="mt-1.5"
              data-testid="setting-sm-phone"
            />
          </div>
          <div>
            <Label htmlFor="dms" className="font-mono-label">DMS Platform</Label>
            <Input
              id="dms"
              value={s.dms_platform || ""}
              onChange={(e) => setS({ ...s, dms_platform: e.target.value })}
              placeholder="CDK Global"
              className="mt-1.5"
              data-testid="setting-dms-platform"
            />
          </div>
          <div>
            <Label htmlFor="dmsm" className="font-mono-label">DMS Connection Method</Label>
            <Input
              id="dmsm"
              value={s.dms_method || ""}
              onChange={(e) => setS({ ...s, dms_method: e.target.value })}
              placeholder="DealerVault feed"
              className="mt-1.5"
              data-testid="setting-dms-method"
            />
          </div>
        </div>
      </Card>

      <Card className="p-5 border-border max-w-4xl mb-6">
        <div className="font-mono-label text-muted-foreground">Coupon Format</div>
        <div className="font-display text-lg font-bold tracking-tight mt-1 mb-3">Prefix &amp; Style</div>
        <div className="grid md:grid-cols-2 gap-4 items-start">
          <div>
            <Label htmlFor="cp" className="font-mono-label">Prefix (letters/numbers, max 8)</Label>
            <Input
              id="cp"
              value={s.coupon_prefix || "DD"}
              onChange={(e) => setS({ ...s, coupon_prefix: e.target.value.toUpperCase().slice(0, 8) })}
              placeholder="DD"
              className="mt-1.5 uppercase"
              data-testid="setting-coupon-prefix"
            />
            <div className="text-xs text-muted-foreground mt-1">e.g. DD, PAG, PRECISN — your dealership initials</div>
            <label className="inline-flex items-center gap-2 mt-4 cursor-pointer">
              <input
                type="checkbox"
                checked={s.coupon_include_name === true}
                onChange={(e) => setS({ ...s, coupon_include_name: e.target.checked })}
                className="w-5 h-5"
                data-testid="setting-coupon-include-name"
              />
              <span className="font-mono-label">Include customer first name in coupon</span>
            </label>
          </div>
          <div className="rounded-md bg-muted/60 border border-border p-3">
            <div className="font-mono-label text-muted-foreground">Live preview (Alicia, {s.stage_1_discount}% off)</div>
            <div className="font-display text-xl font-black text-[hsl(var(--gold))] mt-1 break-all" data-testid="coupon-preview">
              {(s.coupon_prefix || "DD").toUpperCase()}-{s.stage_1_discount}{s.coupon_include_name === true ? "-ALICIA" : ""}-A7B2K9
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6">
        <div className="font-mono-label text-muted-foreground mb-1">Dealership</div>
        <div className="font-display text-lg font-bold tracking-tight mb-4">General</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="dn" className="font-mono-label">Dealership Name</Label>
            <Input
              id="dn"
              value={s.dealership_name}
              onChange={(e) => setS({ ...s, dealership_name: e.target.value })}
              className="mt-1.5"
              data-testid="setting-name"
            />
          </div>
          <div>
            <Label htmlFor="dp" className="font-mono-label">Live Person Phone</Label>
            <Input
              id="dp"
              value={s.dealership_phone}
              onChange={(e) => setS({ ...s, dealership_phone: e.target.value })}
              className="mt-1.5"
              data-testid="setting-phone"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="dlogo" className="font-mono-label">
              Dealership Logo URL <span className="text-muted-foreground/70 font-normal">(optional — shows on the pitch text mockups)</span>
            </Label>
            <div className="flex gap-3 items-start mt-1.5">
              <Input
                id="dlogo"
                value={s.dealership_logo_url || ""}
                onChange={(e) => setS({ ...s, dealership_logo_url: e.target.value })}
                placeholder="https://.../your-logo.png"
                data-testid="setting-logo-url"
                className="flex-1"
              />
              {s.dealership_logo_url ? (
                <div className="w-12 h-12 rounded-md border border-border bg-white grid place-items-center overflow-hidden shrink-0" data-testid="setting-logo-preview">
                  <img
                    src={s.dealership_logo_url}
                    alt="Logo preview"
                    className="w-full h-full object-contain"
                    onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-md border border-dashed border-border grid place-items-center shrink-0 text-[0.55rem] text-muted-foreground text-center leading-tight px-1">
                  no logo
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-1.5 leading-snug">
              Paste any public image URL (PNG or SVG works best). Appears in the header of every
              SMS mockup on the pitch decks — Example Sheet, Live Pitch, Customer Journey.
              <b> Not sent inside the actual text message</b> (MMS costs more and hits Trust Hub review).
            </div>
          </div>
          <div>
            <Label htmlFor="s1d" className="font-mono-label">Offer 1 — Days after decline</Label>
            <Input
              id="s1d"
              type="number"
              value={s.stage_1_days}
              onChange={(e) => setS({ ...s, stage_1_days: e.target.value })}
              className="mt-1.5"
              data-testid="setting-stage1-days"
            />
          </div>
          <div>
            <Label htmlFor="s1p" className="font-mono-label">Offer 1 — Discount %</Label>
            <select
              id="s1p"
              value={s.stage_1_discount}
              onChange={(e) => setS({ ...s, stage_1_discount: Number(e.target.value) })}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="setting-stage1-discount"
            >
              {[10,15,20,25,30,35,40,45,50].map((v) => (
                <option key={v} value={v}>{v}% off</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="s2d" className="font-mono-label">Offer 2 — Days after decline</Label>
            <Input
              id="s2d"
              type="number"
              value={s.stage_2_days}
              onChange={(e) => setS({ ...s, stage_2_days: e.target.value })}
              className="mt-1.5"
              data-testid="setting-stage2-days"
            />
          </div>
          <div>
            <Label htmlFor="s2p" className="font-mono-label">Offer 2 — Discount %</Label>
            <select
              id="s2p"
              value={s.stage_2_discount}
              onChange={(e) => setS({ ...s, stage_2_discount: Number(e.target.value) })}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="setting-stage2-discount"
            >
              {[10,15,20,25,30,35,40,45,50].map((v) => (
                <option key={v} value={v}>{v}% off</option>
              ))}
            </select>
          </div>
        </div>
        {(Number(s.stage_1_discount) > 30 || Number(s.stage_2_discount) > 30) && (
          <div
            className="mt-4 rounded-md bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 text-xs dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200"
            data-testid="discount-warning-stage"
          >
            <b>Higher discounts reduce your gross.</b> Above 30% is a bigger dent per RO —
            use it only when you&apos;re confident the customer wouldn&apos;t come back otherwise.
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">
            Currently saved in DB: <b>Day {savedSnapshot ? JSON.parse(savedSnapshot).stage_1_days : "—"} · {savedSnapshot ? JSON.parse(savedSnapshot).stage_1_discount : "—"}% OFF</b> · <b>Day {savedSnapshot ? JSON.parse(savedSnapshot).stage_2_days : "—"} · {savedSnapshot ? JSON.parse(savedSnapshot).stage_2_discount : "—"}% OFF</b>
          </div>
          <Button
            onClick={quickSaveDiscounts}
            disabled={saving || !dirty}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="save-discounts-btn"
          >
            {saving ? "Saving…" : `Save discounts (${s.stage_1_discount}% / ${s.stage_2_discount}%)`}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6" data-testid="stripe-subscription-card">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <div className="font-mono-label text-muted-foreground">Your Subscription</div>
        </div>
        <div className="font-display text-lg font-bold tracking-tight mb-1">Stripe billing portal</div>
        <div className="text-xs text-muted-foreground mb-4">Update your card, view invoices, or cancel — all handled by Stripe.</div>
        <Button
          type="button"
          onClick={async () => {
            try {
              const r = await api.post("/billing/customer-portal", { return_url: window.location.href });
              if (r.data?.portal_url) window.location.href = r.data.portal_url;
            } catch (e) {
              const msg = e.response?.data?.detail || "Portal unavailable";
              // Explain legacy tenants gracefully — nobody wants a raw 400
              if (msg.includes("No Stripe subscription")) {
                toast("Your account was set up before Stripe billing existed — email jayers.me@gmail.com to migrate onto a subscription.", { duration: 6000 });
              } else {
                toast.error(msg);
              }
            }
          }}
          className="bg-navy hover:bg-navy/85"
          data-testid="manage-subscription-btn"
        >
          Manage subscription →
        </Button>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <div className="font-mono-label text-muted-foreground">Billing</div>
        </div>
        <div className="font-display text-lg font-bold tracking-tight mb-4">Pricing</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="mfee" className="font-mono-label">Monthly Subscription ($)</Label>
            <Input
              id="mfee"
              type="number"
              step="1"
              value={s.monthly_fee ?? 1599}
              onChange={(e) => setS({ ...s, monthly_fee: Number(e.target.value) })}
              className="mt-1.5"
              data-testid="setting-monthly-fee"
            />
            <div className="text-xs text-muted-foreground mt-1">Flat monthly SaaS fee per rooftop</div>
          </div>
          <div>
            <Label htmlFor="cmo" className="font-mono-label">Contract Length (months)</Label>
            <Input
              id="cmo"
              type="number"
              step="1"
              value={s.contract_months ?? 12}
              onChange={(e) => setS({ ...s, contract_months: Number(e.target.value) })}
              className="mt-1.5"
              data-testid="setting-contract-months"
            />
            <div className="text-xs text-muted-foreground mt-1">Set to 0 for month-to-month</div>
          </div>
          <div>
            <Label htmlFor="fee" className="font-mono-label">Recovery Fee % (optional)</Label>
            <Input
              id="fee"
              type="number"
              step="0.1"
              value={s.fee_percent ?? 0}
              onChange={(e) => setS({ ...s, fee_percent: Number(e.target.value) })}
              className="mt-1.5"
              data-testid="setting-fee-percent"
            />
            <div className="text-xs text-muted-foreground mt-1">Set to 0 if you only charge the monthly</div>
          </div>
          <div>
            <Label htmlFor="onb" className="font-mono-label">One-Time Onboarding Fee ($)</Label>
            <Input
              id="onb"
              type="number"
              step="1"
              value={s.onboarding_fee ?? 999}
              onChange={(e) => setS({ ...s, onboarding_fee: Number(e.target.value) })}
              className="mt-1.5"
              data-testid="setting-onboarding-fee"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Appears on first month&apos;s statement. {s.onboarding_billed ? "Already billed." : "Not yet billed."}
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <div className="font-mono-label text-muted-foreground">Remittance</div>
        </div>
        <div className="font-display text-lg font-bold tracking-tight mb-1">How dealers pay you</div>
        <div className="text-xs text-muted-foreground mb-4">Appears on every printed statement. Fill in one or both — check is most common.</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label htmlFor="rname" className="font-mono-label">Make Check Payable To</Label>
            <Input
              id="rname"
              value={s.remit_to_name || ""}
              onChange={(e) => setS({ ...s, remit_to_name: e.target.value })}
              placeholder="Ayers Enterprises"
              className="mt-1.5"
              data-testid="setting-remit-name"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="raddr" className="font-mono-label">Mailing Address (for checks)</Label>
            <textarea
              id="raddr"
              rows={3}
              value={s.remit_to_address || ""}
              onChange={(e) => setS({ ...s, remit_to_address: e.target.value })}
              placeholder={"123 Main Street\nSuite 100\nDallas, TX 75201"}
              className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium"
              data-testid="setting-remit-address"
            />
          </div>
          <div>
            <Label htmlFor="rbank" className="font-mono-label">ACH — Bank Name</Label>
            <Input
              id="rbank"
              value={s.remit_ach_bank || ""}
              onChange={(e) => setS({ ...s, remit_ach_bank: e.target.value })}
              placeholder="Chase Business"
              className="mt-1.5"
              data-testid="setting-ach-bank"
            />
          </div>
          <div>
            <Label htmlFor="rrout" className="font-mono-label">ACH — Routing Number</Label>
            <Input
              id="rrout"
              value={s.remit_ach_routing || ""}
              onChange={(e) => setS({ ...s, remit_ach_routing: e.target.value })}
              placeholder="021000021"
              className="mt-1.5"
              data-testid="setting-ach-routing"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="racct" className="font-mono-label">ACH — Account Number</Label>
            <Input
              id="racct"
              value={s.remit_ach_account || ""}
              onChange={(e) => setS({ ...s, remit_ach_account: e.target.value })}
              placeholder="123456789012"
              className="mt-1.5"
              data-testid="setting-ach-account"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
          <div className="font-mono-label text-muted-foreground">Message Templates</div>
        </div>
        <div className="font-display text-lg font-bold tracking-tight mb-3">SMS Wording</div>

        <div className="rounded-md border border-dashed border-border bg-muted/40 p-3 mb-5">
          <div className="font-mono-label text-muted-foreground mb-2">Available Placeholders</div>
          <div className="flex flex-wrap gap-1.5">
            {PLACEHOLDERS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => navigator.clipboard?.writeText("{" + p.key + "}")}
                className="text-xs font-mono px-2 py-1 rounded bg-background border border-border hover:bg-muted transition-colors"
                title={"Click to copy — " + p.label}
                data-testid={`placeholder-${p.key}`}
              >
                {"{" + p.key + "}"}
              </button>
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-2">Click any tag to copy. Paste it into a template below.</div>
        </div>

        <div className="grid gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-blue-500 text-white">Offer 1 · Day {s.stage_1_days} · {s.stage_1_discount}% OFF</Badge>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_stage_1 || ""}
              onChange={(e) => setS({ ...s, sms_template_stage_1: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-1"
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">Preview to Alicia</div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-1">{preview30}</div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-orange-500 text-white">Offer 2 · Day {s.stage_2_days} · {s.stage_2_discount}% OFF</Badge>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_stage_2 || ""}
              onChange={(e) => setS({ ...s, sms_template_stage_2: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-2"
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">Preview to Alicia</div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-2">{preview40}</div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-sky-500 text-white">Free Oil Change · Manual Blast</Badge>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_oil_change || ""}
              onChange={(e) => setS({ ...s, sms_template_oil_change: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-oil"
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">Preview to Alicia</div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-oil">{previewOil}</div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-amber-500 text-white">Parts Arrived · CSV Upload</Badge>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_parts_arrived || ""}
              onChange={(e) => setS({ ...s, sms_template_parts_arrived: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-parts"
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">Preview to Alicia</div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-parts">{previewParts}</div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-emerald-600 text-white">CSI · Auto {s.csi_auto_send_hours || 24}h after close</Badge>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_csi || ""}
              onChange={(e) => setS({ ...s, sms_template_csi: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-csi"
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">Preview to Alicia</div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-csi">{previewCsi}</div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={`${s.body_shop_enabled ? "bg-rose-600" : "bg-stone-400"} text-white`}>
                Body Shop · {s.body_shop_enabled ? "ON" : "OFF"} · {s.body_shop_discount ?? 10}% OFF
              </Badge>
              <Input
                type="number"
                min="0"
                max="100"
                value={s.body_shop_discount ?? 10}
                onChange={(e) => setS({ ...s, body_shop_discount: Number(e.target.value) })}
                className="w-24 h-7 text-xs"
                data-testid="setting-body-discount"
              />
              <span className="text-xs text-muted-foreground">discount %</span>
              <div className="flex-1" />
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={s.body_shop_enabled === true}
                  onChange={(e) => setS({ ...s, body_shop_enabled: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="setting-body-shop-enabled"
                />
                <span className="font-mono-label text-xs">
                  {s.body_shop_enabled ? "Enabled" : "Disabled"}
                </span>
              </label>
            </div>
            <Textarea
              rows={4}
              value={s.sms_template_body_shop || ""}
              onChange={(e) => setS({ ...s, sms_template_body_shop: e.target.value })}
              className="font-mono text-sm"
              data-testid="setting-template-body"
              disabled={!s.body_shop_enabled}
            />
            <div className="mt-2 p-3 rounded-md bg-muted/60 border border-border">
              <div className="font-mono-label text-muted-foreground mb-1">
                {s.body_shop_enabled ? "Preview to Alicia" : "Preview (disabled)"}
              </div>
              <div className="text-sm leading-relaxed text-foreground font-medium" data-testid="preview-template-body">
                {previewBody}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 border-border max-w-4xl mb-6">
        <div className="font-mono-label text-muted-foreground mb-1">Follow-Up Details</div>
        <div className="font-display text-lg font-bold tracking-tight mb-4">Advisors &amp; CSI Manager</div>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="dpa" className="font-mono-label">Default Parts Advisor</Label>
            <Input
              id="dpa"
              value={s.default_parts_advisor || ""}
              onChange={(e) => setS({ ...s, default_parts_advisor: e.target.value })}
              placeholder="Jayne"
              className="mt-1.5"
              data-testid="setting-parts-advisor"
            />
            <div className="text-xs text-muted-foreground mt-1">
              Used in Parts Arrived + Free Oil Change texts when a customer has no advisor on file.
            </div>
          </div>
          <div>
            <Label htmlFor="csiah" className="font-mono-label">CSI Auto-Send Delay (hours)</Label>
            <Input
              id="csiah"
              type="number"
              min="1"
              value={s.csi_auto_send_hours ?? 24}
              onChange={(e) => setS({ ...s, csi_auto_send_hours: Number(e.target.value) })}
              className="mt-1.5"
              data-testid="setting-csi-hours"
            />
            <label className="inline-flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={s.csi_auto_enabled !== false}
                onChange={(e) => setS({ ...s, csi_auto_enabled: e.target.checked })}
                className="w-5 h-5"
                data-testid="setting-csi-enabled"
              />
              <span className="font-mono-label">Auto-send CSI after close</span>
            </label>
          </div>
          <div>
            <Label htmlFor="csimn" className="font-mono-label">CSI Manager Name</Label>
            <Input
              id="csimn"
              value={s.csi_manager_name || ""}
              onChange={(e) => setS({ ...s, csi_manager_name: e.target.value })}
              placeholder="Tom Tillman"
              className="mt-1.5"
              data-testid="setting-csi-name"
            />
          </div>
          <div>
            <Label htmlFor="csimp" className="font-mono-label">CSI Manager Phone</Label>
            <Input
              id="csimp"
              value={s.csi_manager_phone || ""}
              onChange={(e) => setS({ ...s, csi_manager_phone: e.target.value })}
              placeholder="987-4378-8978"
              className="mt-1.5"
              data-testid="setting-csi-phone"
            />
          </div>
        </div>
      </Card>

      {/* Comeback Protocol — 30/60/90 day post-drip escalation ladder */}
      <Card className="p-6 border-border max-w-4xl" data-testid="comeback-settings-card">
        <div className="font-mono-label text-muted-foreground">Comeback Protocol</div>
        <div className="font-display text-lg font-bold tracking-tight mt-1">
          30 · 60 · 90 Day Escalation
        </div>
        <p className="text-xs text-muted-foreground mt-1 mb-5 max-w-2xl">
          After the Day {s.stage_1_days} ({s.stage_1_discount}% off) and Day {s.stage_2_days} ({s.stage_2_discount}% off) texts, most vendors quit.
          The Comeback Protocol keeps working the decline — a final SMS on Day{" "}
          {s.comeback_day_30_days ?? 30}, an advisor call task on Day{" "}
          {s.comeback_day_60_days ?? 60}, a manager relationship call on Day{" "}
          {s.comeback_day_90_days ?? 90}. Toggle any stage on or off per your team&apos;s capacity.
        </p>

        {/* Comeback final discount picker — shares the same guardrails as
            the two offer stages (max 50%, must be ≥ Offer 2, warn > 30%). */}
        <div className="mb-4 grid md:grid-cols-2 gap-4 items-start p-3 rounded-md bg-muted/40 border border-border">
          <div>
            <Label htmlFor="cb-final-pct" className="font-mono-label">Final Offer · Discount %</Label>
            <select
              id="cb-final-pct"
              value={s.comeback_discount_final ?? 50}
              onChange={(e) => setS({ ...s, comeback_discount_final: Number(e.target.value) })}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              data-testid="setting-comeback-discount-final"
            >
              {[10, 15, 20, 25, 30, 35, 40, 45, 50].map((v) => (
                <option key={v} value={v}>{v}% off</option>
              ))}
            </select>
            <div className="text-[0.65rem] text-muted-foreground mt-1">
              Fires on the Day {s.comeback_day_30_days ?? 30} SMS. Must be equal to or higher than
              Offer 2 ({s.stage_2_discount ?? 40}%). Hard cap 50%.
            </div>
          </div>
          <div className="text-xs">
            {(s.stage_1_discount > 30 || s.stage_2_discount > 30 || (s.comeback_discount_final ?? 50) > 30) && (
              <div
                className="rounded-md bg-amber-50 border border-amber-300 text-amber-900 px-3 py-2 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200"
                data-testid="discount-warning"
              >
                <b>Higher discounts reduce your gross.</b> Above 30% is a bigger dent per RO —
                worth it only when you&apos;re confident the customer wouldn&apos;t come back otherwise.
              </div>
            )}
          </div>
        </div>

        {[
          { key: "comeback_day_30_enabled", days_key: "comeback_day_30_days", label: `Day ${s.comeback_day_30_days ?? 30} · Final SMS`, sub: `Auto-fires a ${s.comeback_discount_final ?? 50}% last-chance text with a unique coupon` },
          { key: "comeback_day_60_enabled", days_key: "comeback_day_60_days", label: `Day ${s.comeback_day_60_days ?? 60} · Advisor Call Task`, sub: "Creates a task on the Comebacks page assigned to the advisor" },
          { key: "comeback_day_90_enabled", days_key: "comeback_day_90_days", label: `Day ${s.comeback_day_90_days ?? 90} · Manager Escalation`, sub: "Escalates to your Service Manager for a relationship call" },
        ].map(({ key, days_key, label, sub }) => (
          <div
            key={key}
            className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-b-0"
            data-testid={`comeback-toggle-row-${key}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">{label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Input
                type="number"
                min={1}
                max={365}
                value={s[days_key] ?? (key.includes("30") ? 30 : key.includes("60") ? 60 : 90)}
                onChange={(e) => setS({ ...s, [days_key]: Number(e.target.value) || 30 })}
                className="w-20 h-9 text-center font-mono"
                data-testid={`comeback-days-${key}`}
              />
              <span className="font-mono-label text-muted-foreground text-[0.6rem]">days</span>
              <Button
                type="button"
                size="sm"
                variant={s[key] !== false ? "default" : "outline"}
                onClick={() => setS({ ...s, [key]: !(s[key] !== false) })}
                className={s[key] !== false ? "bg-emerald-600 hover:bg-emerald-700 text-white min-w-[70px]" : "min-w-[70px]"}
                data-testid={`comeback-enabled-${key}`}
              >
                {s[key] !== false ? "ON" : "OFF"}
              </Button>
            </div>
          </div>
        ))}

        <div className="mt-5">
          <Label htmlFor="cbfinal" className="font-mono-label">
            Day {s.comeback_day_30_days ?? 30} SMS template · {s.comeback_discount_final ?? 50}% OFF
          </Label>
          <Textarea
            id="cbfinal"
            rows={3}
            value={s.sms_template_comeback_final || ""}
            onChange={(e) => setS({ ...s, sms_template_comeback_final: e.target.value })}
            className="mt-1 font-mono text-xs"
            data-testid="comeback-template-input"
          />
          <div className="text-[0.65rem] text-muted-foreground mt-1">
            Placeholders: {"{first_name}, {vehicle}, {services}, {dealership}, {coupon_code}, {schedule_url}, {call_url}"}
          </div>
        </div>
      </Card>

      {/* SMS Compliance — TCPA quiet hours, Sunday toggle, STOP/HELP evidence */}
      <Card className="p-6 border-border max-w-4xl" data-testid="sms-compliance-card">
        <div className="font-mono-label text-muted-foreground">SMS Compliance</div>
        <div className="font-display text-lg font-bold tracking-tight mt-1">
          Quiet Hours · STOP/HELP · Consent Trail
        </div>
        <p className="text-xs text-muted-foreground mt-1 mb-5 max-w-2xl">
          TCPA rails: nothing sends outside the window you set. Everything
          queued during quiet hours drains automatically the moment the window
          opens. STOP and HELP inbound keywords are auto-handled per CTIA.
          Live Twilio sending stays OFF until you flip <b>SMS_LIVE_DISABLED=0</b>{" "}
          in the backend env — nothing goes to a real phone before then.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between gap-4 py-3 border-b border-border md:border-b-0" data-testid="quiet-hours-toggle-row">
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">Enforce quiet hours</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Block outbound texts outside the window below. Compliance replies (STOP/HELP) still go through.
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant={s.quiet_hours_enabled !== false ? "default" : "outline"}
              onClick={() => setS({ ...s, quiet_hours_enabled: !(s.quiet_hours_enabled !== false) })}
              className={s.quiet_hours_enabled !== false ? "bg-emerald-600 hover:bg-emerald-700 text-white min-w-[70px]" : "min-w-[70px]"}
              data-testid="quiet-hours-enabled-toggle"
            >
              {s.quiet_hours_enabled !== false ? "ON" : "OFF"}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-4 py-3 border-b border-border md:border-b-0" data-testid="send-sundays-toggle-row">
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">Send on Sundays</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Most dealers leave this OFF. Sunday messages get flagged as spam far more often.
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant={s.send_sundays === true ? "default" : "outline"}
              onClick={() => setS({ ...s, send_sundays: !(s.send_sundays === true) })}
              className={s.send_sundays === true ? "bg-emerald-600 hover:bg-emerald-700 text-white min-w-[70px]" : "min-w-[70px]"}
              data-testid="send-sundays-toggle"
            >
              {s.send_sundays === true ? "ON" : "OFF"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div>
            <Label htmlFor="qh-start" className="font-mono-label">Start hour (local)</Label>
            <Input
              id="qh-start"
              type="number"
              min={0}
              max={23}
              value={s.quiet_hours_start ?? 9}
              onChange={(e) => setS({ ...s, quiet_hours_start: Number(e.target.value) })}
              className="mt-1.5 font-mono text-center"
              data-testid="quiet-hours-start-input"
            />
            <div className="text-[0.65rem] text-muted-foreground mt-1">Default 9 = 9am</div>
          </div>
          <div>
            <Label htmlFor="qh-end" className="font-mono-label">End hour (local)</Label>
            <Input
              id="qh-end"
              type="number"
              min={1}
              max={24}
              value={s.quiet_hours_end ?? 19}
              onChange={(e) => setS({ ...s, quiet_hours_end: Number(e.target.value) })}
              className="mt-1.5 font-mono text-center"
              data-testid="quiet-hours-end-input"
            />
            <div className="text-[0.65rem] text-muted-foreground mt-1">Default 19 = 7pm</div>
          </div>
          <div>
            <Label htmlFor="qh-tz" className="font-mono-label">Dealer timezone</Label>
            <Input
              id="qh-tz"
              value={s.quiet_hours_timezone || "America/Chicago"}
              onChange={(e) => setS({ ...s, quiet_hours_timezone: e.target.value })}
              placeholder="America/Chicago"
              className="mt-1.5 font-mono text-xs"
              data-testid="quiet-hours-timezone-input"
            />
            <div className="text-[0.65rem] text-muted-foreground mt-1">
              IANA name (e.g. America/Chicago)
            </div>
          </div>
        </div>

        <div className="mt-5 p-3 rounded-md border border-border bg-muted/30">
          <div className="font-mono-label text-[0.6rem] tracking-widest text-muted-foreground mb-1">
            HOW STOP / HELP ARE HANDLED
          </div>
          <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
            <li>Any inbound <b>STOP / UNSUBSCRIBE / CANCEL / END / QUIT</b> — number added to the opt-out list instantly, all future sends suppressed.</li>
            <li>Any inbound <b>START / YES</b> — number removed from opt-out.</li>
            <li>Any inbound <b>HELP / INFO / SUPPORT</b> — auto-replies with dealership name, msg&data-rates disclosure, and STOP instructions (CTIA required).</li>
            <li>Every outbound send is stamped in <code>sms_consent_log</code> with the consent basis (existing-customer relationship).</li>
          </ul>
        </div>
      </Card>

      {/* Consent capture in RO process — dealer attests they collect opt-in */}
      <Card className="p-6 border-border max-w-4xl" data-testid="ro-consent-card">
        <div className="font-mono-label text-muted-foreground">Consent Capture</div>
        <div className="font-display text-lg font-bold tracking-tight mt-1">
          RO Intake Confirms Text Opt-In
        </div>
        <p className="text-xs text-muted-foreground mt-1 mb-5 max-w-2xl">
          Decline Drive will NEVER text a customer that isn't on file as consented.
          Consent must come from a real source — a check-box in your DMS RO template,
          a signed printed intake sheet you upload, or the customer texting START.
          Toggle this ON to attest, on the record, that your service advisors are
          asking every customer during check-in and marking their opt-in in the DMS.
          Nothing else changes about sending — this is your dealership's paper trail.
        </p>
        <div className="flex items-center justify-between gap-4 py-3 border-b border-border" data-testid="ro-consent-toggle-row">
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm">Our RO check-in captures text opt-in</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {s.ro_consent_confirmed ? (
                <>Confirmed <b>{s.ro_consent_confirmed_at ? new Date(s.ro_consent_confirmed_at).toLocaleDateString() : "—"}</b>{s.ro_consent_confirmed_by ? <> by <b>{s.ro_consent_confirmed_by}</b></> : null}</>
              ) : (
                <>Flip this ON once your team is capturing opt-in on every RO</>
              )}
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant={s.ro_consent_confirmed === true ? "default" : "outline"}
            onClick={() => setS({ ...s, ro_consent_confirmed: !(s.ro_consent_confirmed === true) })}
            className={s.ro_consent_confirmed === true ? "bg-emerald-600 hover:bg-emerald-700 text-white min-w-[100px]" : "min-w-[100px]"}
            data-testid="ro-consent-toggle"
          >
            {s.ro_consent_confirmed === true ? "CONFIRMED" : "NOT YET"}
          </Button>
        </div>
        <div className="mt-4">
          <Label htmlFor="ro-consent-notes" className="font-mono-label">Notes on your intake process</Label>
          <Textarea
            id="ro-consent-notes"
            rows={3}
            value={s.ro_consent_process_notes || ""}
            onChange={(e) => setS({ ...s, ro_consent_process_notes: e.target.value })}
            placeholder="e.g. Advisor checks the SMS opt-in box on CDK line 12 of every RO. Printed pad backup at the desk if DMS is down."
            className="mt-1 font-mono text-xs"
            data-testid="ro-consent-notes-input"
          />
          <div className="text-[0.65rem] text-muted-foreground mt-1">
            Auditor evidence — describe exactly HOW your advisors capture consent so a plaintiff-attorney fishing letter has an immediate answer.
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-border">
          <div className="font-mono-label text-[0.6rem] tracking-widest text-muted-foreground mb-1">
            UPLOAD YOUR CONSENTED CUSTOMER LIST
          </div>
          <div className="font-display font-bold text-sm mb-1">Consent CSV import</div>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            Drop a CSV of <b>only the customers who have already opted in</b>
            (your DMS opt-in export, a signed-intake batch, etc.). Required
            column: <code>phone</code>. Optional: <code>source_detail</code>,{" "}
            <code>name</code>. Every row is stamped with source=<code>csv_upload</code>,
            your email as <code>granted_by</code>, and today's date. Existing
            consent rows are never overwritten — safe to run repeatedly.
            <br />
            <b className="text-amber-700">Do NOT upload every customer that has ever had an RO.</b>
            {" "}Only upload documented opt-ins.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              id="consent-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setConsentFile(e.target.files?.[0] || null)}
              className="flex-1"
              data-testid="consent-file-input"
            />
            <Button
              type="button"
              size="sm"
              disabled={!consentFile || consentUploading}
              onClick={async () => {
                if (!consentFile) return;
                setConsentUploading(true);
                try {
                  const fd = new FormData();
                  fd.append("file", consentFile);
                  const { data } = await api.post("/consent/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  toast.success(`Granted consent on ${data.granted} customers (skipped ${data.skipped} of ${data.total_rows}).`);
                  setConsentFile(null);
                  const input = document.getElementById("consent-file");
                  if (input) input.value = "";
                } catch (e) {
                  toast.error(formatError(e.response?.data?.detail) || "Upload failed.");
                } finally {
                  setConsentUploading(false);
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white min-w-[160px]"
              data-testid="consent-upload-btn"
            >
              {consentUploading ? "Uploading…" : "Upload consent CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <Button
        onClick={save}
        disabled={saving || !dirty}
        className="max-w-4xl"
        data-testid="save-settings-btn"
      >
        {saving ? "Saving…" : dirty ? "Save all changes" : "All changes saved ✓"}
      </Button>
    </DashboardShell>
  );
}
